import React from 'react';
import { Settings, Home, BarChart2 } from 'lucide-react';

const AdditionalParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Дополнительные параметры
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Налоговый вычет */}
        <div className="col-span-full">
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="useDeduction"
                checked={formData.useDeduction}
                onChange={handleInputChange}
                className="form-checkbox"
                disabled={!['apartment', 'room', 'house', 'cottage'].includes(formData.propertyType)}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Использовать налоговый вычет
              </span>
            </label>
            {!['apartment', 'room', 'house', 'cottage'].includes(formData.propertyType) && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Вычет не применяется к данному типу имущества
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Налоговый вычет уменьшает налогооблагаемую площадь: 20 кв.м. для квартир, 10 кв.м. для комнат, 50 кв.м. для домов
          </p>
        </div>

        {/* Понижающий коэффициент для новостроек */}
        <div className="col-span-full mt-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="useReducingCoefficient"
              checked={formData.useReducingCoefficient}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Применять понижающий коэффициент для новостроек
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            В течение первых 5 лет с момента постройки применяются понижающие коэффициенты: 0.2, 0.4, 0.6, 0.8, 1
          </p>
        </div>
        
        {/* Учет износа объекта */}
        <div className="col-span-full mt-2">
          <div className="flex items-start">
            <div className="flex h-5 items-center">
              <input
                type="checkbox"
                name="includeDepreciation"
                id="includeDepreciation"
                checked={formData.includeDepreciation}
                onChange={handleInputChange}
                className="form-checkbox"
              />
            </div>
            <div className="ml-2">
              <label htmlFor="includeDepreciation" className="text-sm text-gray-700 dark:text-gray-300">
                Учитывать износ объекта
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Применяется только в некоторых случаях по решению суда или экспертизы
              </p>
            </div>
          </div>
        </div>
        
        {/* Процент износа */}
        {formData.includeDepreciation && (
          <div className="col-span-full mt-2 pl-7">
            <label htmlFor="depreciationPercent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Процент износа объекта
            </label>
            <div className="flex items-center">
              <input
                type="text"
                id="depreciationPercent"
                name="depreciationPercent"
                value={formData.depreciationPercent}
                onChange={handleInputChange}
                className={`form-input w-full max-w-xs ${errors.depreciationPercent ? 'input-error' : ''}`}
                placeholder="Введите процент износа"
              />
              <span className="ml-2 text-gray-600 dark:text-gray-400">%</span>
            </div>
            {errors.depreciationPercent && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.depreciationPercent}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Информация о понижающих коэффициентах */}
      {formData.useReducingCoefficient && (
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
          <div className="flex items-start">
            <BarChart2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                Понижающие коэффициенты для новостроек
              </h4>
              <div className="grid grid-cols-5 gap-2 mb-2">
                <div className="text-center bg-white dark:bg-gray-700 rounded p-1 border border-indigo-100 dark:border-indigo-800">
                  <div className="text-xs text-gray-600 dark:text-gray-400">1-й год</div>
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">0.2</div>
                </div>
                <div className="text-center bg-white dark:bg-gray-700 rounded p-1 border border-indigo-100 dark:border-indigo-800">
                  <div className="text-xs text-gray-600 dark:text-gray-400">2-й год</div>
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">0.4</div>
                </div>
                <div className="text-center bg-white dark:bg-gray-700 rounded p-1 border border-indigo-100 dark:border-indigo-800">
                  <div className="text-xs text-gray-600 dark:text-gray-400">3-й год</div>
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">0.6</div>
                </div>
                <div className="text-center bg-white dark:bg-gray-700 rounded p-1 border border-indigo-100 dark:border-indigo-800">
                  <div className="text-xs text-gray-600 dark:text-gray-400">4-й год</div>
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">0.8</div>
                </div>
                <div className="text-center bg-white dark:bg-gray-700 rounded p-1 border border-indigo-100 dark:border-indigo-800">
                  <div className="text-xs text-gray-600 dark:text-gray-400">5-й год</div>
                  <div className="font-bold text-indigo-600 dark:text-indigo-400">1.0</div>
                </div>
              </div>
              <p className="text-xs text-indigo-600 dark:text-indigo-400">
                Применяется автоматически в течение первых 5 лет с года постройки объекта. 
                Коэффициент 0.2 означает, что вы платите только 20% от рассчитанного налога.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalParametersSection;